/** Shared reference voice capture — upload + microphone with auto-upload / auto-select. */

import { AudioOutlined, FolderOpenOutlined } from '@ant-design/icons'
import { message } from 'antd'
import { memo, useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { SaasApiError } from '../../../../api'
import {
  EFFECTIVE_MIN_DURATION_SECONDS,
  type VoiceReferenceQualityAnalysis,
} from '../../../../voiceReference/voiceReferenceQualityPolicy'
import { analyzeReferenceAudioBlob, formatDurationClock } from '../../../../voiceReference/voiceReferenceQuality'
import { canUseVoiceReferenceSample } from '../../../../voiceReference/voiceReferenceReadiness'
import { VOICE_REFERENCE_READING_I18N_KEYS } from '../../../../voiceReference/voiceReferenceReadingSamples'
import { useVoiceMicrophoneRecording } from '../../../../voiceReference/useVoiceMicrophoneRecording'
import {
  getVoiceReferenceRecordingDraft,
  hasVoiceReferenceRecordingDraft,
  type VoiceReferenceRecordingDraftStatus,
} from '../../../../voiceReference/voiceReferenceRecordingDraftStore'
import VoiceAudioPlayer from '../../../virtualIp/VoiceAudioPlayer'
import VoiceRecordingReadinessChecklist from './VoiceRecordingReadinessChecklist'

type CaptureMethod = 'UPLOAD' | 'MICROPHONE' | 'LIBRARY'
type InputMode = 'upload' | 'record' | null
type ReadingMode = 'sample' | 'freeform'

export type VoiceReferenceCaptureValue = {
  assetId: string
  filename: string
  durationMs: number
  captureMethod: CaptureMethod
  quality?: VoiceReferenceQualityAnalysis
  playbackUrl?: string | null
}

type Props = {
  nodeId: string
  disabled?: boolean
  referenceAssetId: string | null
  referenceFilename: string | null
  referenceDurationMs: number | null
  referenceCaptureMethod?: CaptureMethod | null
  referencePlaybackUrl?: string | null
  onSelectFromLibrary: () => void
  onReferenceConfirmed: (value: VoiceReferenceCaptureValue) => void
  onReferenceCleared: () => void
  onDraftStatusChange?: (status: VoiceReferenceRecordingDraftStatus) => void
  uploadReference: (
    file: File,
    durationMs: number,
    captureMethod: 'UPLOAD' | 'MICROPHONE',
  ) => Promise<{ asset_id: string; filename: string; duration_ms: number; playback_url?: string }>
}

function qualityMessage(
  analysis: VoiceReferenceQualityAnalysis,
  t: ReturnType<typeof useTranslation<'canvas'>>['t'],
): string | null {
  if (analysis.status !== 'BLOCKED') return null

  switch (analysis.failureCode) {
    case 'NO_AUDIO_DATA':
      return t('voiceTemplate.referenceCapture.quality.captureFailed')
    case 'DECODE_FAILED':
      return t('voiceTemplate.referenceCapture.quality.decodeFailed')
    case 'TOO_SHORT':
      return t('voiceTemplate.referenceCapture.quality.tooShort', {
        seconds: Math.max(1, Math.round(analysis.durationMs / 1000)),
        min: EFFECTIVE_MIN_DURATION_SECONDS,
      })
    case 'LOW_VOLUME':
      return t('voiceTemplate.referenceCapture.quality.lowVolume')
    case 'NO_SPEECH':
    case 'TOO_MUCH_SILENCE':
      return t('voiceTemplate.referenceCapture.quality.noSpeech')
    case 'CLIPPING':
      return t('voiceTemplate.referenceCapture.quality.clippingHigh')
    default:
      return t('voiceTemplate.referenceCapture.quality.blocked')
  }
}

function recordingErrorMessage(
  errorKey: string,
  t: ReturnType<typeof useTranslation<'canvas'>>['t'],
): string {
  if (errorKey === 'captureFailed') return t('voiceTemplate.referenceCapture.quality.captureFailed')
  if (errorKey === 'unsupportedFormat') return t('voiceTemplate.referenceCapture.permission.unsupportedFormat')
  return t(`voiceTemplate.referenceCapture.permission.${errorKey}`)
}

function buildRecordingFile(blob: Blob): File {
  const rawType = (blob.type || 'audio/webm').split(';', 1)[0]!.trim() || 'audio/webm'
  const ext = rawType.includes('ogg') ? 'ogg' : rawType.includes('mp4') ? 'm4a' : 'webm'
  return new File([blob], `microphone-recording.${ext}`, { type: rawType })
}

function VoiceReferenceCapturePanelImpl({
  nodeId,
  disabled,
  referenceAssetId,
  referenceFilename,
  referenceDurationMs,
  referenceCaptureMethod,
  referencePlaybackUrl,
  onSelectFromLibrary,
  onReferenceConfirmed,
  onReferenceCleared,
  onDraftStatusChange,
  uploadReference,
}: Props) {
  const { t, i18n } = useTranslation('canvas')
  const initialDraft = getVoiceReferenceRecordingDraft(nodeId)
  const [inputMode, setInputMode] = useState<InputMode>(null)
  const [readingMode, setReadingMode] = useState<ReadingMode>('sample')
  const [candidateQuality, setCandidateQuality] = useState<VoiceReferenceQualityAnalysis | null>(
    () => initialDraft?.quality ?? null,
  )
  const [uploading, setUploading] = useState(() => initialDraft?.status === 'uploading')
  const [analyzingRecording, setAnalyzingRecording] = useState(false)
  const [uploadErrorKey, setUploadErrorKey] = useState<string | null>(() => initialDraft?.uploadErrorKey ?? null)
  const [uploadErrorDetail, setUploadErrorDetail] = useState<string | null>(
    () => initialDraft?.uploadErrorDetail ?? null,
  )
  const recording = useVoiceMicrophoneRecording(nodeId)
  const frozenReadingLocaleRef = useRef<string | null>(null)
  const autoUploadStartedForBlobRef = useRef<Blob | null>(null)
  const uploadInFlightRef = useRef(false)

  const readingLocale = frozenReadingLocaleRef.current || i18n.language
  const sampleText = t(VOICE_REFERENCE_READING_I18N_KEYS.sampleText, { lng: readingLocale })

  useEffect(() => {
    if (recording.phase === 'recording' && !frozenReadingLocaleRef.current) {
      frozenReadingLocaleRef.current = i18n.language
    }
    if (recording.phase === 'idle' || recording.phase === 'recorded') {
      frozenReadingLocaleRef.current = null
    }
  }, [i18n.language, recording.phase])

  const publishDraftStatus = useCallback(() => {
    const draft = getVoiceReferenceRecordingDraft(nodeId)
    onDraftStatusChange?.(draft?.status ?? 'idle')
  }, [nodeId, onDraftStatusChange])

  useEffect(() => {
    publishDraftStatus()
  }, [
    publishDraftStatus,
    recording.phase,
    recording.blob,
    uploading,
    analyzingRecording,
    uploadErrorKey,
    referenceAssetId,
  ])

  const hasLocalDraft = hasVoiceReferenceRecordingDraft(nodeId) || recording.phase === 'recorded'

  const uploadRecordingBlob = useCallback(
    async (blob: Blob, analysis: VoiceReferenceQualityAnalysis) => {
      if (uploadInFlightRef.current) return
      if (!canUseVoiceReferenceSample(analysis)) return
      uploadInFlightRef.current = true
      setUploading(true)
      setUploadErrorKey(null)
      setUploadErrorDetail(null)
      recording.persistDraft({
        status: 'uploading',
        quality: analysis,
        uploadErrorKey: null,
        uploadErrorDetail: null,
      })
      publishDraftStatus()
      try {
        const file = buildRecordingFile(blob)
        const res = await uploadReference(file, analysis.durationMs, 'MICROPHONE')
        onReferenceConfirmed({
          assetId: res.asset_id,
          filename: res.filename || file.name,
          durationMs: res.duration_ms ?? analysis.durationMs,
          captureMethod: 'MICROPHONE',
          quality: analysis,
          playbackUrl: res.playback_url ?? null,
        })
        recording.persistDraft({
          status: 'ready_remote',
          uploadErrorKey: null,
          uploadErrorDetail: null,
        })
        // Keep local blob until reset so closing modal mid-success is safe; clear after confirm.
        recording.resetRecording()
        setCandidateQuality(null)
        setUploadErrorKey(null)
        setUploadErrorDetail(null)
        setInputMode(null)
        message.success(t('voiceTemplate.referenceCapture.recording.readyRemote'))
      } catch (err) {
        const detail = (err as SaasApiError).message || t('voiceTemplate.referenceCapture.recording.uploadFailed')
        setUploadErrorKey('uploadFailed')
        setUploadErrorDetail(detail)
        recording.persistDraft({
          status: 'upload_failed',
          quality: analysis,
          uploadErrorKey: 'uploadFailed',
          uploadErrorDetail: detail,
        })
        message.error(detail)
      } finally {
        uploadInFlightRef.current = false
        setUploading(false)
        publishDraftStatus()
      }
    },
    [onReferenceConfirmed, publishDraftStatus, recording, t, uploadReference],
  )

  const analyzeRecordedBlob = useCallback(async () => {
    if (!recording.blob) return
    setAnalyzingRecording(true)
    recording.persistDraft({ status: 'analyzing', uploadErrorKey: null, uploadErrorDetail: null })
    publishDraftStatus()
    try {
      const analysis = await analyzeReferenceAudioBlob(recording.blob)
      setCandidateQuality(analysis)
      const ready = canUseVoiceReferenceSample(analysis)
      recording.persistDraft({
        quality: analysis,
        status: ready ? 'ready_local' : 'recorded_local',
        uploadErrorKey: null,
        uploadErrorDetail: null,
      })
      setUploadErrorKey(null)
      setUploadErrorDetail(null)
      publishDraftStatus()
      if (ready && recording.blob && autoUploadStartedForBlobRef.current !== recording.blob) {
        autoUploadStartedForBlobRef.current = recording.blob
        void uploadRecordingBlob(recording.blob, analysis)
      }
    } finally {
      setAnalyzingRecording(false)
      publishDraftStatus()
    }
  }, [publishDraftStatus, recording, uploadRecordingBlob])

  useEffect(() => {
    if (recording.phase === 'recorded' && recording.blob) {
      const draft = getVoiceReferenceRecordingDraft(nodeId)
      if (draft?.quality && draft.status === 'upload_failed') {
        setCandidateQuality(draft.quality)
        setUploadErrorKey(draft.uploadErrorKey)
        setUploadErrorDetail(draft.uploadErrorDetail)
        return
      }
      if (draft?.quality && (draft.status === 'ready_local' || draft.status === 'uploading' || draft.status === 'ready_remote')) {
        setCandidateQuality(draft.quality)
        if (
          draft.status === 'ready_local' &&
          canUseVoiceReferenceSample(draft.quality) &&
          autoUploadStartedForBlobRef.current !== recording.blob
        ) {
          autoUploadStartedForBlobRef.current = recording.blob
          void uploadRecordingBlob(recording.blob, draft.quality)
        }
        return
      }
      if (draft?.quality && draft.status === 'recorded_local') {
        setCandidateQuality(draft.quality)
        return
      }
      void analyzeRecordedBlob()
    }
    if (recording.phase === 'idle' || recording.phase === 'recording' || recording.phase === 'requesting') {
      if (!hasVoiceReferenceRecordingDraft(nodeId)) {
        setCandidateQuality(null)
        setAnalyzingRecording(false)
        setUploadErrorKey(null)
        setUploadErrorDetail(null)
        autoUploadStartedForBlobRef.current = null
      }
    }
  }, [analyzeRecordedBlob, nodeId, recording.blob, recording.phase, uploadRecordingBlob])

  const handleRetryUpload = useCallback(() => {
    const draft = getVoiceReferenceRecordingDraft(nodeId)
    const blob = recording.blob || draft?.blob
    const quality = candidateQuality || draft?.quality
    if (!blob || !quality) return
    autoUploadStartedForBlobRef.current = blob
    void uploadRecordingBlob(blob, quality)
  }, [candidateQuality, nodeId, recording.blob, uploadRecordingBlob])

  const handleCloseRecordingPanel = useCallback(() => {
    if (recording.phase === 'recording' || recording.phase === 'requesting' || recording.phase === 'finalizing') {
      recording.cancelRecording()
      setCandidateQuality(null)
      autoUploadStartedForBlobRef.current = null
    }
    // Do not cancel in-flight upload — continues in background via refs/draft store.
    setInputMode(null)
  }, [recording])

  const activeLabel = referenceFilename || referenceAssetId

  const readinessMode =
    recording.phase === 'recording' || recording.phase === 'finalizing'
      ? 'live'
      : recording.phase === 'recorded' || candidateQuality
        ? 'final'
        : 'idle'

  const draftStatus = getVoiceReferenceRecordingDraft(nodeId)?.status ?? null
  const showPreparing =
    uploading || analyzingRecording || draftStatus === 'uploading' || draftStatus === 'analyzing'
  const showReadyRemote = Boolean(referenceAssetId) && !showPreparing && !uploadErrorKey

  return (
    <div className="pcv-voice-reference-capture nodrag nowheel" data-testid="canvas-voice-reference-capture">
      <div className="pcv-voice-section-label">{t('voiceTemplate.referenceCapture.title')}</div>
      <div className="pcv-voice-section-hint">{t('voiceTemplate.referenceCapture.subtitle')}</div>
      <div className="pcv-voice-reference-actions">
        <button
          type="button"
          className="pcv-voice-reference-action-btn"
          disabled={disabled || uploading}
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => {
            e.stopPropagation()
            onSelectFromLibrary()
          }}
          data-testid="canvas-voice-reference-select-btn"
        >
          <FolderOpenOutlined />
          <span>{t('voiceTemplate.referenceCapture.selectReferenceAudio')}</span>
        </button>
        <button
          type="button"
          className={`pcv-voice-reference-action-btn${inputMode === 'record' ? ' is-active' : ''}`}
          disabled={disabled || uploading}
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => {
            e.stopPropagation()
            setInputMode('record')
          }}
          data-testid="canvas-voice-reference-record-btn"
        >
          <AudioOutlined />
          <span>{t('voiceTemplate.referenceCapture.recordMicrophone')}</span>
        </button>
      </div>

      {referenceAssetId && activeLabel ? (
        <div className="pcv-voice-reference-active-card" data-testid="canvas-voice-reference-active">
          <div className="pcv-voice-reference-active-main">
            <span className="pcv-voice-reference-active-name">{activeLabel}</span>
            {referenceDurationMs ? (
              <span className="pcv-voice-reference-active-meta">
                {formatDurationClock(referenceDurationMs)}
                {referenceCaptureMethod === 'MICROPHONE'
                  ? ` · ${t('voiceTemplate.referenceCapture.captureMethod.microphone')}`
                  : referenceCaptureMethod === 'UPLOAD'
                    ? ` · ${t('voiceTemplate.referenceCapture.captureMethod.upload')}`
                    : referenceCaptureMethod === 'LIBRARY'
                      ? ` · ${t('voiceTemplate.referenceCapture.captureMethod.library')}`
                      : null}
              </span>
            ) : null}
            {showReadyRemote ? (
              <span className="pcv-voice-reference-ready-badge" data-testid="canvas-voice-reference-ready">
                {t('voiceTemplate.referenceCapture.recording.readyRemote')}
              </span>
            ) : null}
          </div>
          {referencePlaybackUrl ? (
            <div className="pcv-voice-reference-active-player nodrag" onClick={(e) => e.stopPropagation()}>
              <VoiceAudioPlayer playbackUrl={referencePlaybackUrl} testId="canvas-voice-reference-player" />
            </div>
          ) : null}
          <div className="pcv-voice-reference-active-actions">
            <button
              type="button"
              className="pcv-gen-tool-btn"
              disabled={disabled || uploading}
              onClick={(e) => {
                e.stopPropagation()
                onSelectFromLibrary()
              }}
              data-testid="canvas-voice-reference-replace-btn"
            >
              {t('voiceTemplate.referenceCapture.replace')}
            </button>
            <button
              type="button"
              className="pcv-gen-ref-chip-remove"
              disabled={disabled || uploading}
              onClick={(e) => {
                e.stopPropagation()
                onReferenceCleared()
              }}
              data-testid="canvas-voice-reference-remove-btn"
            >
              {t('voiceTemplate.referenceCapture.remove')}
            </button>
          </div>
        </div>
      ) : null}

      {showPreparing && !referenceAssetId ? (
        <div className="pcv-voice-recording-status" data-testid="canvas-voice-reference-uploading">
          {analyzingRecording || draftStatus === 'analyzing'
            ? t('voiceTemplate.referenceCapture.recording.analyzing')
            : t('voiceTemplate.referenceCapture.recording.uploadingReference')}
        </div>
      ) : null}

      {uploadErrorKey && !referenceAssetId ? (
        <div className="pcv-voice-field-error" data-testid="canvas-voice-recording-upload-error">
          {uploadErrorDetail || t(`voiceTemplate.referenceCapture.recording.${uploadErrorKey}`)}
        </div>
      ) : null}

      {hasLocalDraft && inputMode !== 'record' && recording.previewUrl ? (
        <div className="pcv-voice-recording-draft-card" data-testid="canvas-voice-recording-draft">
          <div className="pcv-voice-recording-review-title">
            {uploading
              ? t('voiceTemplate.referenceCapture.recording.uploadingReference')
              : uploadErrorKey
                ? t('voiceTemplate.referenceCapture.recording.uploadFailed')
                : t('voiceTemplate.referenceCapture.recording.draftSaved')}
          </div>
          <audio className="pcv-voice-recording-preview-audio nodrag" controls src={recording.previewUrl} />
          {candidateQuality ? (
            <VoiceRecordingReadinessChecklist
              mode="final"
              layout="responsive-inline"
              durationMs={candidateQuality.durationMs}
              analysis={candidateQuality}
            />
          ) : null}
          <div className="pcv-voice-recording-review-actions">
            <button type="button" className="pcv-gen-tool-btn" onClick={() => setInputMode('record')}>
              {t('voiceTemplate.referenceCapture.recording.openDraft')}
            </button>
            {uploadErrorKey ? (
              <button
                type="button"
                className="pcv-gen-run-btn"
                disabled={uploading || analyzingRecording}
                onClick={handleRetryUpload}
                data-testid="canvas-voice-recording-retry-upload"
              >
                {t('voiceTemplate.referenceCapture.recording.retryUpload')}
              </button>
            ) : null}
            <button
              type="button"
              className="pcv-gen-tool-btn"
              disabled={uploading}
              onClick={() => {
                recording.resetRecording()
                setCandidateQuality(null)
                setUploadErrorKey(null)
                setUploadErrorDetail(null)
                autoUploadStartedForBlobRef.current = null
              }}
            >
              {t('voiceTemplate.referenceCapture.recording.recordAgain')}
            </button>
          </div>
        </div>
      ) : null}

      {inputMode === 'record' ? (
        <div className="pcv-voice-recording-panel" data-testid="canvas-voice-recording-panel">
          <div className="pcv-voice-recording-panel-header">
            <span>{t('voiceTemplate.referenceCapture.recording.title')}</span>
            <button type="button" className="pcv-gen-inline-link" onClick={handleCloseRecordingPanel}>
              {t('voiceTemplate.referenceCapture.recording.close')}
            </button>
          </div>
          <p className="pcv-voice-section-hint">{t('voiceTemplate.referenceCapture.recording.intro')}</p>

          <div className="pcv-voice-reading-mode-row">
            <label className="pcv-voice-reading-mode-option">
              <input
                type="radio"
                name={`reading-mode-${nodeId}`}
                checked={readingMode === 'sample'}
                disabled={recording.phase === 'recording'}
                onChange={() => setReadingMode('sample')}
              />
              <span>{t('voiceTemplate.referenceCapture.readingMode.sample')}</span>
            </label>
            <label className="pcv-voice-reading-mode-option">
              <input
                type="radio"
                name={`reading-mode-${nodeId}`}
                checked={readingMode === 'freeform'}
                disabled={recording.phase === 'recording'}
                onChange={() => setReadingMode('freeform')}
              />
              <span>{t('voiceTemplate.referenceCapture.readingMode.freeform')}</span>
            </label>
          </div>

          {readingMode === 'sample' ? (
            <div className="pcv-voice-reading-sample-card pcv-scroll-surface">
              <div className="pcv-voice-reading-sample-label">{t('voiceTemplate.referenceCapture.readingSample.label')}</div>
              <p className="pcv-voice-reading-sample-text">{sampleText}</p>
              <p className="pcv-voice-section-hint">{t(VOICE_REFERENCE_READING_I18N_KEYS.sampleHint)}</p>
            </div>
          ) : (
            <p className="pcv-voice-section-hint">
              {t(VOICE_REFERENCE_READING_I18N_KEYS.freeformHint, { min: EFFECTIVE_MIN_DURATION_SECONDS })}
            </p>
          )}

          <div className="pcv-voice-recording-meter-row" aria-live="polite">
            <span className="pcv-voice-recording-timer" data-testid="canvas-voice-recording-timer">
              {recording.durationLabel}
            </span>
            <div className="pcv-voice-recording-level-track" aria-hidden="true">
              <div className="pcv-voice-recording-level-fill" style={{ width: `${Math.round(recording.level * 100)}%` }} />
            </div>
          </div>

          {recording.phase === 'requesting' ? (
            <div className="pcv-voice-recording-status">{t('voiceTemplate.referenceCapture.recording.requesting')}</div>
          ) : null}

          {recording.phase === 'recording' ? (
            <div className="pcv-voice-recording-status pcv-voice-recording-status--live">
              <span>{t('voiceTemplate.referenceCapture.recording.live')}</span>
              <span>{recording.durationLabel}</span>
            </div>
          ) : null}

          {recording.phase === 'finalizing' ? (
            <div className="pcv-voice-recording-status">{t('voiceTemplate.referenceCapture.recording.finalizing')}</div>
          ) : null}

          {recording.phase === 'error' && recording.errorKey ? (
            <div className="pcv-voice-field-error">{recordingErrorMessage(recording.errorKey, t)}</div>
          ) : null}

          <div className="pcv-voice-recording-actions">
            {recording.phase === 'idle' || recording.phase === 'error' ? (
              <button
                type="button"
                className="pcv-gen-run-btn"
                disabled={disabled}
                onClick={() => void recording.startRecording()}
                data-testid="canvas-voice-recording-start"
              >
                {t('voiceTemplate.referenceCapture.recording.start')}
              </button>
            ) : null}
            {recording.phase === 'recording' ? (
              <>
                <button type="button" className="pcv-gen-tool-btn" onClick={recording.cancelRecording}>
                  {t('voiceTemplate.referenceCapture.recording.cancel')}
                </button>
                <button
                  type="button"
                  className="pcv-gen-run-btn"
                  onClick={recording.stopRecording}
                  data-testid="canvas-voice-recording-stop"
                >
                  {t('voiceTemplate.referenceCapture.recording.stop')}
                </button>
              </>
            ) : null}
          </div>

          {recording.phase === 'recorded' && recording.previewUrl ? (
            <div className="pcv-voice-recording-review">
              <div className="pcv-voice-recording-review-title">
                {analyzingRecording
                  ? t('voiceTemplate.referenceCapture.recording.analyzing')
                  : uploading
                    ? t('voiceTemplate.referenceCapture.recording.uploadingReference')
                    : uploadErrorKey
                      ? t('voiceTemplate.referenceCapture.recording.uploadFailed')
                      : t('voiceTemplate.referenceCapture.recording.ready')}
              </div>
              <audio
                className="pcv-voice-recording-preview-audio nodrag"
                controls
                src={recording.previewUrl}
                data-testid="canvas-voice-recording-preview"
              />
              <VoiceRecordingReadinessChecklist
                mode={readinessMode}
                layout="responsive-inline"
                durationMs={
                  candidateQuality ? candidateQuality.durationMs : recording.durationMs
                }
                live={null}
                analysis={candidateQuality}
              />
              {candidateQuality && qualityMessage(candidateQuality, t) ? (
                <div className="pcv-voice-field-error">{qualityMessage(candidateQuality, t)}</div>
              ) : null}
              {uploadErrorKey ? (
                <div className="pcv-voice-field-error" data-testid="canvas-voice-recording-upload-error">
                  {uploadErrorDetail || t(`voiceTemplate.referenceCapture.recording.${uploadErrorKey}`)}
                </div>
              ) : null}
              <div className="pcv-voice-recording-review-actions">
                <button
                  type="button"
                  className="pcv-gen-tool-btn"
                  disabled={uploading}
                  onClick={() => {
                    recording.resetRecording()
                    setCandidateQuality(null)
                    setUploadErrorKey(null)
                    setUploadErrorDetail(null)
                    autoUploadStartedForBlobRef.current = null
                  }}
                >
                  {t('voiceTemplate.referenceCapture.recording.recordAgain')}
                </button>
                {uploadErrorKey ? (
                  <button
                    type="button"
                    className="pcv-gen-run-btn"
                    disabled={uploading || analyzingRecording}
                    onClick={handleRetryUpload}
                    data-testid="canvas-voice-recording-retry-upload"
                  >
                    {t('voiceTemplate.referenceCapture.recording.retryUpload')}
                  </button>
                ) : null}
              </div>
            </div>
          ) : (
            <VoiceRecordingReadinessChecklist
              mode={readinessMode}
              layout="responsive-inline"
              durationMs={recording.durationMs}
              live={recording.phase === 'recording' ? recording.liveStats : null}
              analysis={null}
            />
          )}

          <p className="pcv-voice-section-hint">{t('voiceTemplate.referenceCapture.recording.privacy')}</p>
        </div>
      ) : null}
    </div>
  )
}

const VoiceReferenceCapturePanel = memo(VoiceReferenceCapturePanelImpl)
export default VoiceReferenceCapturePanel
